/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from 'relements/build/esm/ui/button';
import Loader from 'relements/build/esm/ui/loader';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import Tooltip from 'relements/build/esm/overlays/tooltip';
import Toggle from 'relements/build/esm/input/toggle';

import BotBuilderContants from '@src/phrases';

import {
  Warning,
  Blocker,
  Success,
  ToggleWrapper,
  SuccessWrapper,
  Wrapper,
} from './Components';
import LoginButton from './LoginButton';
import * as constants from './constants';
import initFB from './utils';

const LoginIntegration = ({
  storedValues,
  platformId,
  updateDeploymentValues,
  appId,
}) => {
  const doesUsernameExist = !!storedValues.credentials.username;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState(
    doesUsernameExist ? storedValues.credentials.username : ''
  );
  const [active, setActive] = useState(storedValues.active);
  const [igData, setIgData] = useState({});
  const [showLogin, setShowLogin] = useState(!doesUsernameExist);
  const [fetchLoginData, setFetchLoginData] = useState(false);
  const KEYWORDS = BotBuilderContants.getPhrase;

  useEffect(() => {
    if (doesUsernameExist) {
      initFB({ appId }); // initialising FB if user is already loggedin
    }
  }, []);

  useEffect(() => {
    if (fetchLoginData) {
      getLoginData();
      setFetchLoginData(false);
    }
  }, [fetchLoginData]);

  const getInstagramId = (id, fbusername, permissionsAccepted) => {
    FB.api(
      `/${id}/`,
      'GET',
      { fields: 'instagram_business_account,description,name' },
      (response) => {
        if (response.error || !response.instagram_business_account) {
          setError('invalidInstagramAccount');
          setLoading(false);
        } else {
          if (!permissionsAccepted) {
            setError('permissionsDenied');
            setLoading(false);
            return;
          }
          const { instagram_business_account: igAccount } = response;
          setLoading(false);
          setShowLogin(false);
          setError('');
          const pageData = {
            page_id: id,
            instagram_business_account: igAccount.id,
            username: fbusername,
            ...igData,
          };
          setIgData(pageData);
          setActive(true);
          updateDeploymentValues(platformId, pageData, true, true);
        }
      }
    );
  };

  const getLoginData = () => {
    setLoading(true);
    FB.api(
      '/me',
      'GET',
      { fields: 'name,accounts,permissions' },
      (response) => {
        if (response.permissions) {
          const { permissions, accounts, name } = response;
          const permissionsAccepted = permissions.data.every(
            (per) => per.status === 'granted'
          );
          if (!accounts || accounts?.data.length === 0) {
            setError('noPageSelected');
            setLoading(false);
          } else if (accounts?.data.length === 1) {
            const { id } = accounts?.data?.[0];
            getInstagramId(id, name, permissionsAccepted);
            setUsername(name);
          } else {
            setError('multiplePages');
            setLoading(false);
          }
        }
      }
    );
  };

  /**
   * this api would clear the permissions taken by haptik at facebook end
   */
  const resetIntegration = () => {
    setLoading(true);
    FB.api('/me/permissions', 'DELETE', {}, () => {
      setShowLogin(true);
      setLoading(false);
      setError('');
      const resetPageData = {
        access_token: '',
        page_id: '',
        instagram_business_account: '',
        username: '',
      };
      updateDeploymentValues(platformId, resetPageData, false, true);
    });
  };

  /**
   *
   * @param {object} res
   * @returns
   * facebook callback function needs to be returned for the login window to be closed
   * wrapping setState in timeout so that return is executed before any other affect
   */
  const loginStatus = (res) => {
    if (!showLogin) return; // not checking login details when we have stored data
    if (res.status === 'connected') {
      setTimeout(() => {
        setIgData({ access_token: res.authResponse.accessToken });
        setFetchLoginData(true);
      }, 10);
      return;
    }
    setShowLogin(true);
  };

  const renderErrorMessage = () => {
    const errorMsg = constants.MESSAGES[error];
    if (errorMsg.type === 'warning') {
      return <Warning message={errorMsg.message} />;
    }
    if (errorMsg.type === 'blocker') {
      return <Blocker message={errorMsg.message} />;
    }
    return null;
  };

  const toggleOnChange = (val) => {
    setActive(val);
    updateDeploymentValues(platformId, igData, val, false);
  };

  const successfulLogin = () => {
    const successMsg = constants.MESSAGES.loggedin;
    return (
      <SuccessWrapper>
        <Box
          padding="56px 0px"
          flexDirection="column"
          width="100%"
          alignItems="center"
        >
          <Success message={successMsg.message} />
          <Box padding={0} pb={16}>
            <Text variant="body.lg">You have been logged in as </Text>
            <Box padding={0} pl={6}>
              <Text variant="h3.semi-bold">{username}</Text>
            </Box>
          </Box>
          <Tooltip tooltip="Reset to default Facebook permissions" themed>
            <Button onClick={resetIntegration} themed variant="primary">
              Reset
            </Button>
          </Tooltip>
        </Box>
        <ToggleWrapper>
          <Toggle label="Active" value={active} onChange={toggleOnChange} />
        </ToggleWrapper>
      </SuccessWrapper>
    );
  };

  if (loading) {
    return (
      <Wrapper>
        <Loader themed />
      </Wrapper>
    );
  }

  return (
    <>
      {showLogin ? (
        <Wrapper>
          {error ? (
            renderErrorMessage()
          ) : (
            <Box padding={0} pb={20}>
              <Text variant="body.md">
                To deploy Haptik {KEYWORDS('IVA.UPPERCASE')} on Instagram,
                please login on your Facebook Business Account
              </Text>
            </Box>
          )}
          <LoginButton
            loginCallback={loginStatus}
            buttonText={!error ? 'login_with' : 'continue_with'}
            appId={appId}
          />
        </Wrapper>
      ) : (
        successfulLogin()
      )}
    </>
  );
};

LoginIntegration.propTypes = {
  storedValues: PropTypes.shape({}).isRequired,
  platformId: PropTypes.number.isRequired,
  updateDeploymentValues: PropTypes.func.isRequired,
  appId: PropTypes.string.isRequired,
};

export default LoginIntegration;
